import { Toggle, pantryColors, pantryTypography } from '@pantry/ui'
import type React from 'react'

const p = pantryColors
const F = pantryTypography.fontFamily

interface LoyaltyProgramTogglePanelProps {
  enabled: boolean
  saving: boolean
  onChange: (next: boolean) => void
}

const LoyaltyIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg
    width={18}
    height={18}
    fill="none"
    stroke={color}
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <polyline points="20 12 20 22 4 22 4 12" />
    <rect x="2" y="7" width="20" height="5" />
    <line x1="12" y1="22" x2="12" y2="7" />
    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
  </svg>
)

/**
 * Store-level loyalty program on/off panel shown at the top of the Points tab.
 * Modeled on the Location detail enable/disable fulfillment cards: an icon tile,
 * title + subtitle, an Enabled/Disabled status pill, and a switch pinned right.
 * The switch drives the store's `enable_bs_loyalty` flag (see useBsLoyaltyToggle).
 */
export const LoyaltyProgramTogglePanel: React.FC<
  LoyaltyProgramTogglePanelProps
> = ({ enabled, saving, onChange }) => (
  <div
    style={{
      background: p.white,
      border: `1px solid ${p.gray[200]}`,
      borderRadius: 10,
      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      padding: '16px 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 16,
      fontFamily: F,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: enabled ? p.brand.light : p.gray[100],
        }}
      >
        <LoyaltyIcon color={enabled ? p.brand.primary : p.gray[400]} />
      </div>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: enabled ? p.gray[800] : p.gray[500],
            }}
          >
            Loyalty Program
          </span>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '2px 10px',
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 999,
              background: enabled ? p.teal[50] : p.gray[100],
              color: enabled ? p.teal[700] : p.gray[500],
            }}
          >
            {enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
        <div style={{ fontSize: 12.5, color: p.gray[500], marginTop: 2 }}>
          Enable to let customers earn and redeem points at this store
        </div>
      </div>
    </div>
    <Toggle checked={enabled} onChange={onChange} disabled={saving} />
  </div>
)
